import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandPalette as SharedCommandPalette,
  type CommandItem,
} from './CommandPaletteModal';
import {
  HomeRegular,
  WifiSettingsRegular,
  CameraRegular,
  ToolboxRegular,
  SignOutRegular,
  WeatherSunnyRegular,
  WeatherMoonRegular,
} from '@fluentui/react-icons';
import { useAuth } from '../../hooks/useAuth';
import { useAppRole } from '../../hooks/useAppRole';
import { setThemePreference, useThemePreference } from '../../themes/themeMode';
import { NAV_ITEMS } from './navItems';

/**
 * Map the shared component's HTS palette CSS variables to Site Survey's own
 * HTS tokens (defined in src/index.css and themes/htsTokens.ts). The palette
 * picks these up via :root so it matches the app's existing surface palette.
 */
const SS_PALETTE_THEME = `
:root {
  --surface-overlay: var(--hts-surface);
  --surface-sunken:  var(--hts-surface-sunken);
  --border-default:  var(--hts-hairline-strong);
  --border-subtle:   var(--hts-hairline);
  --text-primary:    var(--hts-ink);
  --text-tertiary:   var(--hts-ink-muted);
  --text-accent:     var(--hts-aqua);
  --accent-soft:     var(--hts-app-bg);
  --elev-popover:    var(--hts-shadow-overlay);
  --hts-radius-lg:   12px;
  --hts-radius-md:   6px;
  --hts-font-size-base: 14px;
  --hts-font-size-sm:   13px;
  --hts-font-size-xs:   11px;
}
`;

// Extra pages that aren't in the rail (the rail intentionally only surfaces
// Home / Projects / P2P per the HTS app-shell standard). Keep these in
// one place so the palette and the actual routes can never disagree.
const EXTRA_PAGES: Array<{ path: string; label: string; Icon: typeof HomeRegular; keywords: string }> = [
  { path: '/devices',        label: 'Devices',        Icon: WifiSettingsRegular, keywords: 'ap camera switch meraki' },
  { path: '/security-survey',label: 'Security Survey',Icon: CameraRegular,        keywords: 'camera survey install' },
  { path: '/install',        label: 'Install',        Icon: ToolboxRegular,      keywords: 'install field tech' },
];

export function CommandPalette() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const role = useAppRole();
  const theme = useThemePreference();

  const [open, setOpen] = useState(false);
  const [, setQuery] = useState('');

  // ⌘K / Ctrl+K toggles, Escape closes. Listening on document so the
  // palette works from any focused element (input, canvas, etc.).
  useEffect(() => {
    function down(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const items = useMemo<CommandItem[]>(() => {
    const close = () => {
      setOpen(false);
      setQuery('');
    };

    const navItems: CommandItem[] = NAV_ITEMS.map((n) => ({
      id: `nav-${n.path}`,
      label: n.label,
      group: 'Navigation',
      icon: <n.icon />,
      keywords: `${n.label} ${n.path.replace(/^\//, '')} navigate page`,
      perform: () => {
        close();
        navigate(n.path);
      },
    }));

    const extraNavItems: CommandItem[] = EXTRA_PAGES.map((p) => ({
      id: `nav-${p.path}`,
      label: p.label,
      group: 'Navigation',
      icon: <p.Icon />,
      keywords: `${p.label} ${p.keywords} navigate page`,
      perform: () => {
        close();
        navigate(p.path);
      },
    }));

    const actions: CommandItem[] = [
      {
        id: 'act-theme',
        label: `Switch theme (current: ${theme})`,
        group: 'Actions',
        icon: theme === 'dark' ? <WeatherSunnyRegular /> : <WeatherMoonRegular />,
        keywords: 'dark light mode appearance toggle theme',
        perform: () => {
          close();
          setThemePreference(theme === 'dark' ? 'light' : 'dark');
        },
      },
    ];
    if (role) {
      actions.push({
        id: 'act-signout',
        label: 'Sign out',
        group: 'Actions',
        icon: <SignOutRegular />,
        keywords: 'logout log out exit',
        perform: async () => {
          close();
          await signOut();
        },
      });
    }

    return [...navItems, ...extraNavItems, ...actions];
  }, [navigate, signOut, role, theme]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: SS_PALETTE_THEME }} />
      <SharedCommandPalette
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        onQueryChange={setQuery}
        placeholder="Search pages, actions…"
      />
    </>
  );
}
